// pages/DashboardPage.js
// Dashboard principale ABITALO CRM - Role-Based
// Aggiornato: 28 Maggio 2026

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardApi } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Users, FileText, FileCheck, Files, Receipt, Building2, Plus,
  ChevronRight, AlertCircle, TrendingUp, Calendar, CheckCircle,
  Send, FileEdit, Euro, MapPin, Clock
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';
import DirezioneDashboard from './DirezioneDashboard';
import AdministrationDashboard from './AdministrationDashboard';
import ProjectManagerDashboard from './ProjectManagerDashboard';
import TechnicianDashboard from './TechnicianDashboard';

// Mappa icone per i KPI dinamici
const iconMap = {
  'users': Users,
  'file-text': FileText,
  'file-check': FileCheck,
  'files': Files,
  'receipt': Receipt,
  'building': Building2,
  'calendar': Calendar,
  'check-circle': CheckCircle,
  'send': Send,
  'file-edit': FileEdit,
  'euro': Euro,
  'map-pin': MapPin,
  'clock': Clock,
  'alert-circle': AlertCircle
};

// Colori per i KPI
const colorMap = {
  'blue': { bg: 'bg-blue-50', text: 'text-blue-600' },
  'amber': { bg: 'bg-amber-50', text: 'text-amber-600' },
  'orange': { bg: 'bg-orange-50', text: 'text-orange-600' },
  'purple': { bg: 'bg-purple-50', text: 'text-purple-600' },
  'green': { bg: 'bg-green-50', text: 'text-green-600' },
  'emerald': { bg: 'bg-emerald-50', text: 'text-emerald-600' }
};

// ==================== COMPONENTI KPI ====================

const DynamicKPICard = ({ kpi }) => {
  const Icon = iconMap[kpi.icon] || FileText;
  const colors = colorMap[kpi.color] || colorMap.blue;

  // Card content shared between Link and non-Link (fallback) render.
  const content = (
    <>
      <div className="flex items-start justify-between">
        <div className={`w-11 h-11 ${colors.bg} rounded-xl flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${colors.text}`} />
        </div>
        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#FF7819] transition-colors" />
      </div>

      <div className="mt-4">
        <p className="text-sm text-gray-500 font-medium">{kpi.title}</p>

        {/* Mostra count o value */}
        {kpi.value !== undefined && kpi.value !== null && kpi.value > 0 ? (
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatCurrency(kpi.value)}
          </p>
        ) : (
          <p className="text-3xl font-bold text-gray-900 mt-1">{kpi.count}</p>
        )}

        {/* SAL medio */}
        {kpi.avg_sal !== undefined && kpi.avg_sal !== null && (
          <p className="text-sm text-gray-500 mt-1">
            SAL medio {kpi.avg_sal}%
          </p>
        )}

        {/* Sottotitolo */}
        {kpi.subtitle && !kpi.avg_sal && (
          <p className="text-sm text-gray-400 mt-1">{kpi.subtitle}</p>
        )}
      </div>
    </>
  );

  // Se kpi.link è definito il card è un vero <Link> (a11y-corretto, funziona
  // anche prima dell'hydration, supporta right-click/middle-click, non
  // dipende da un onClick handler programmatico che può essere intercettato
  // da overlay/estensioni browser). Fallback a <div> non cliccabile se link mancante.
  const baseCls = "block bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-all group";

  if (kpi.link) {
    return (
      <Link
        to={kpi.link}
        className={`${baseCls} cursor-pointer no-underline text-inherit`}
        data-testid={`kpi-${kpi.id}`}
      >
        {content}
      </Link>
    );
  }

  return (
    <div className={baseCls} data-testid={`kpi-${kpi.id}`}>
      {content}
    </div>
  );
};

// ==================== AZIONI URGENTI ====================

const UrgentActionsSection = ({ actions = [] }) => {
  const navigate = useNavigate();

  if (actions.length === 0) {
    return (
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm h-full">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-[#FF7819]" />
            Azioni Urgenti
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mb-3">
              <CheckCircle className="w-7 h-7 text-green-500" />
            </div>
            <p className="text-sm font-medium text-gray-700">Nessuna azione urgente</p>
            <p className="text-xs text-gray-400 mt-1">Tutto sotto controllo</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white rounded-xl border border-gray-100 shadow-sm h-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-[#FF7819]" />
          Azioni Urgenti
          <span className="ml-auto px-2 py-0.5 bg-[#FF7819] text-white text-xs font-bold rounded-full">
            {actions.reduce((sum, a) => sum + a.count, 0)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1">
          {actions.map((action, idx) => {
            const IconComponent = iconMap[action.icon] || AlertCircle;
            return (
              <div
                key={idx}
                onClick={() => navigate(action.link)}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-4 h-4 text-orange-600" />
                  </div>
                  <span className="text-sm text-gray-700">{action.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full">
                    {action.count}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#FF7819] transition-colors" />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== PIPELINE ====================

const stageColors = [
  { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' }
];

const PipelineSection = ({ pipeline = [], showValues = true }) => {
  const navigate = useNavigate();
  
  if (!pipeline || pipeline.length === 0) {
    return null;
  }
  
  const totalValue = pipeline.reduce((sum, stage) => sum + (stage.value || 0), 0);
  const totalCount = pipeline.reduce((sum, stage) => sum + stage.count, 0);

  return (
    <Card className="bg-white rounded-xl border border-gray-100 shadow-sm h-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#FF7819]" />
          Pipeline Commerciale
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex gap-2 overflow-x-auto pb-3">
          {pipeline.map((stage, idx) => {
            const colors = stageColors[idx % stageColors.length];
            return (
              <div
                key={stage.status}
                onClick={() => navigate(`/leads?status=${stage.status}`)}
                className={`flex-1 min-w-[100px] p-3 rounded-lg ${colors.bg} border ${colors.border} cursor-pointer hover:shadow-sm transition-all`}
              >
                <p className={`text-xs font-medium ${colors.text} truncate`}>{stage.label}</p>
                <p className={`text-xl font-bold ${colors.text} mt-1`}>{stage.count}</p>
                {showValues && stage.value > 0 && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {formatCurrency(stage.value)}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        
        <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-2">
          <div>
            <p className="text-xs text-gray-500">Valore pipeline totale</p>
            <p className="text-lg font-bold text-gray-900">{formatCurrency(totalValue)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Lead totali</p>
            <p className="text-lg font-bold text-gray-900">{totalCount}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== RECENT ITEMS ====================

const statusColors = {
  nuovo: 'bg-blue-100 text-blue-800',
  contattato: 'bg-cyan-100 text-cyan-800',
  appuntamento_fissato: 'bg-amber-100 text-amber-800',
  sopralluogo_effettuato: 'bg-orange-100 text-orange-800',
  in_preventivazione: 'bg-yellow-100 text-yellow-800',
  preventivo_presentato: 'bg-purple-100 text-purple-800',
  in_trattativa: 'bg-indigo-100 text-indigo-800',
  in_contratto: 'bg-pink-100 text-pink-800',
  perso: 'bg-red-100 text-red-800',
  convertito: 'bg-green-100 text-green-800',
  bozza: 'bg-gray-100 text-gray-800',
  presentato: 'bg-amber-100 text-amber-800',
  accettato: 'bg-green-100 text-green-800'
};

const RecentItemsSection = ({ items = [], title = "Lead Recenti" }) => {
  if (!items || items.length === 0) {
    return (
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm h-full">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-[#FF7819]" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-sm text-gray-500">Nessun elemento recente</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white rounded-xl border border-gray-100 shadow-sm h-full">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Users className="w-5 h-5 text-[#FF7819]" />
          {title}
        </CardTitle>
        <Link to="/leads" className="text-sm text-[#FF7819] hover:underline flex items-center gap-1" data-testid="recent-items-tutti-link">
          Tutti <ChevronRight className="w-4 h-4" />
        </Link>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {items.map((item, idx) => (
            <Link
              key={item.id || idx}
              to={item.link || '#'}
              className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group no-underline text-inherit"
              data-testid={`recent-item-${item.id || idx}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-medium text-sm">
                  {item.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[item.status] || 'bg-gray-100 text-gray-700'}`}>
                      {item.status_label}
                    </span>
                    {item.subtitle && (
                      <span className="text-xs text-gray-400">{item.subtitle}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {item.value && (
                  <span className="text-sm font-medium text-gray-700">{formatCurrency(item.value)}</span>
                )}
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#FF7819] transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== APPUNTAMENTI ====================

const AppointmentsSection = ({ appointments = [] }) => {
  const navigate = useNavigate();
  
  if (!appointments || appointments.length === 0) {
    return (
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm h-full">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#FF7819]" />
            Prossimi Appuntamenti
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-sm text-gray-500">Nessun appuntamento in programma</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white rounded-xl border border-gray-100 shadow-sm h-full">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#FF7819]" />
          Prossimi Appuntamenti
        </CardTitle>
        <Link to="/calendar" className="text-sm text-[#FF7819] hover:underline flex items-center gap-1">
          Tutti <ChevronRight className="w-4 h-4" />
        </Link>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {appointments.map((appt, idx) => (
            <div
              key={appt.id || idx}
              onClick={() => navigate(appt.lead_id ? `/leads/${appt.lead_id}` : '/calendar')}
              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors group ${
                appt.is_today ? 'bg-orange-50 border border-orange-200' : 
                appt.is_overdue ? 'bg-red-50 border border-red-200' : 
                'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  appt.is_today ? 'bg-orange-100' : 
                  appt.is_overdue ? 'bg-red-100' : 
                  'bg-gray-100'
                }`}>
                  <Calendar className={`w-5 h-5 ${
                    appt.is_today ? 'text-orange-600' : 
                    appt.is_overdue ? 'text-red-600' : 
                    'text-gray-500'
                  }`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{appt.client_name}</p>
                  <p className="text-xs text-gray-500">
                    {appt.type_label} • {appt.date} {appt.time}
                    {appt.is_today && <span className="ml-1 text-orange-600 font-medium">OGGI</span>}
                    {appt.is_overdue && <span className="ml-1 text-red-600 font-medium">SCADUTO</span>}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#FF7819] transition-colors" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== MAIN DASHBOARD ====================

const LegacyDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const res = await dashboardApi.getSummary();
      setSummary(res.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Errore nel caricamento della dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Errore nel caricamento della dashboard</p>
      </div>
    );
  }

  const { 
    dashboard_type, 
    title, 
    subtitle, 
    kpis = [], 
    urgent_actions = [], 
    pipeline, 
    recent_items = [], 
    upcoming_appointments = [],
    permissions = {}
  } = summary;

  // Titolo sezione lead in base al tipo dashboard
  const recentItemsTitle = dashboard_type === 'furniture' 
    ? 'I Miei Lead Arredi' 
    : dashboard_type === 'technician'
    ? 'I Miei Lead'
    : 'Lead Recenti';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* ==================== HEADER ==================== */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {title || `Benvenuto, ${user?.first_name}`}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {subtitle || 'Ecco cosa richiede attenzione oggi'}
          </p>
        </div>
        
        {/* Bottoni azione in base ai permessi */}
        <div className="flex gap-2">
          {permissions.can_create_lead && (
            <Link to="/leads/new">
              <Button 
                className="bg-[#FF7819] hover:bg-[#e06a15] text-white shadow-sm"
                data-testid="new-lead-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Lead
              </Button>
            </Link>
          )}
          {permissions.can_create_furniture_quote && dashboard_type === 'furniture' && (
            <Link to="/arredi">
              <Button 
                variant="outline"
                className="border-[#FF7819] text-[#FF7819] hover:bg-[#FF7819]/5"
                data-testid="new-furniture-quote-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Preventivo Arredi
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* ==================== KPI DINAMICI ==================== */}
      <div className={`grid gap-4 ${
        kpis.length <= 4 ? 'grid-cols-2 md:grid-cols-4' :
        kpis.length <= 6 ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6' :
        'grid-cols-2 md:grid-cols-4'
      }`}>
        {kpis.map((kpi) => (
          <DynamicKPICard key={kpi.id} kpi={kpi} />
        ))}
      </div>

      {/* ==================== AZIONI URGENTI + PIPELINE ==================== */}
      <div className={`grid gap-6 ${permissions.show_pipeline && pipeline ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
        <UrgentActionsSection actions={urgent_actions} />
        {permissions.show_pipeline && pipeline && (
          <PipelineSection 
            pipeline={pipeline} 
            showValues={permissions.show_payments} 
          />
        )}
      </div>

      {/* ==================== LEAD RECENTI + APPUNTAMENTI ==================== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentItemsSection 
          items={recent_items} 
          title={recentItemsTitle}
        />
        <AppointmentsSection appointments={upcoming_appointments} />
      </div>
    </div>
  );
};

// ==================== ROLE-AWARE SELECTOR ====================
// Framework dashboard role-aware: la Direzione/Admin riceve la Dashboard V2
// (control room); gli altri ruoli mantengono la dashboard corrente (fallback
// sicuro) finché le rispettive dashboard V2 non vengono costruite.
const DashboardPage = () => {
  const { isAdmin, user } = useAuth();
  if (isAdmin) {
    return <DirezioneDashboard />;
  }
  if (user?.role === 'administration') {
    return <AdministrationDashboard />;
  }
  if (user?.role === 'project_manager') {
    return <ProjectManagerDashboard />;
  }
  if (user?.role === 'external_technician') {
    return <TechnicianDashboard />;
  }
  return <LegacyDashboard />;
};

export default DashboardPage;
